import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { AppBreadcrumbComponent } from "../../../layouts/full/shared/breadcrumb/breadcrumb.component";
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LookupService } from 'src/app/services/lookup.service';
import { ClinicalitemDetailComponent } from './detail/clinicalitem-detail.component';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { debounceTime, distinctUntilChanged, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { PaginationResult } from 'src/app/models/common.response';
import { ClinicalItemService } from './clinical-item.service';
import { DeleteDialog } from '../../dialogs/delete.component';
import { MatPaginator } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IClinicalItem, IClinicalItemListRequest } from './iclinical-item';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-clinical-item',
  standalone: true,
  imports: [MaterialModule, CommonModule, TablerIconsModule, AppBreadcrumbComponent, ReactiveFormsModule, TranslateModule],
  templateUrl: './clinical-item.component.html',
  styleUrl: './clinical-item.component.scss'
})
export class ClinicalItemComponent implements OnInit, AfterViewInit {
  dataSource = new MatTableDataSource<IClinicalItem>();
  displayedColumns: string[] = [
    'description',
    'additionalInfo',
    'category',
    'subcategory',
    'isSince',
    'action',
  ];
  subcategoryList: CommonDropDown[];
  categoryList: CommonDropDown[];
  subcategoryId: string | null;
  selectedsubcategory: CommonDropDown | undefined;
  selectedcategory: CommonDropDown | undefined;
  pagination: PaginationResult;
  subcategorycount: number = 0;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  clinicalItemdata: IClinicalItem[];
  categoryId: string | null;
  isLoading: boolean = false;
  searchItem: FormControl = new FormControl();
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);

  constructor(
    private dialog: MatDialog,
    private _lookupService: LookupService,
    private _itemService: ClinicalItemService,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private translationUtils: TranslationUtils
  ) { }

  ngOnInit(): void {
    this.subcategoryId = this.activatedRoute.snapshot.paramMap.get('id');
    this.categoryId = this.activatedRoute.snapshot.paramMap.get('categoryid');

    this._lookupService.getSubCategory(Number(this.categoryId))
      .subscribe((data) => {
        this.subcategoryList = data;
        this.selectedsubcategory = this.subcategoryList.find(x => x.id == Number(this.subcategoryId));
      })

    this._lookupService.getCategory()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((category: CommonDropDown[]) => {
        this.categoryList = category;
        this.selectedcategory = this.categoryList.find(x => x.id == Number(this.categoryId));
      });

    this._itemService.pagination$
      .pipe(takeUntil(this._unsubscribeAll),)
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.subcategorycount = this.pagination.rowCount;
        }
      });

    this._itemService.clinicalitems$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.dataSource.data = data;
        this.clinicalItemdata = data;
      });

    this.searchItem.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        return this._itemService.getClinicalItem({
          pageNumber: 0,
          pageSize: this.pagination.pageSize,
          isDescending: false,
          searchValue: query || '',
          categoryId: Number(this.categoryId),
          subcategoryID: Number(this.subcategoryId)
        }).pipe(
          finalize(() => (this.isLoading = false))
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.clinicalItems;
        this.subcategorycount = response.result.pagination.rowCount;
      }
    });

  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  onItemAdd() {
    this.dialog.open(ClinicalitemDetailComponent, {
      autoFocus: false,
      width: '700px',
      height: 'auto',
      disableClose: true,
      data: {
        Subcategoryvalue: this.selectedsubcategory,
        categoryvalue: this.selectedcategory
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshClinicalItem();
      }
    });
  }

  onItemUpdate(items: IClinicalItem) {
    this.dialog.open(ClinicalitemDetailComponent, {
      autoFocus: false,
      width: '700px',
      height: 'auto',
      disableClose: true,
      data: {
        clinicalitems: items,
        isEdit: true,
        Subcategoryvalue: this.selectedsubcategory,
        categoryvalue: this.selectedcategory,
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshClinicalItem();
      }
    });
  }

  onDeleteClinicalItem(id: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.CLINICALITEM') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._itemService.deleteClinicalItem(id).subscribe((response) => {
          if (response.isSuccess) {
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.CLINICALITEM')));
            this.refreshClinicalItem();
          }
          else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          }
        })
      }
    });
  }

  getSubCategoryName(subcategoryId: number): string {
    if (!this.subcategoryList) return '-';
    const category = this.subcategoryList.find(cat => cat.id === subcategoryId);
    return category ? category.name : '-';
  }

  getCategoryName(categoryId: number): string {
    if (!this.categoryList) return 'Unknown';
    const category = this.categoryList.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  }

  refreshClinicalItem() {
    const globalSearchValue = this.searchItem?.value?.trim();
    const requestPayload: IClinicalItemListRequest = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false,
      categoryId: Number(this.categoryId),
      subcategoryID: Number(this.subcategoryId)
    };

    if (globalSearchValue) {
      requestPayload.searchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    this._itemService.getClinicalItem(requestPayload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        if (response && response.result) {
          const subcategory = response.result.clinicalItems;
          this.clinicalItemdata = subcategory;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(subcategory);
          } else {
            this.dataSource.data = subcategory;
          }
          this.dataSource.paginator = this.paginator;
          this.subcategorycount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });
  }

}
